import "@andypf/json-viewer";

import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, inject } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from "@angular/material/dialog";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import type { PlanData } from "../../../../model/plan.types";

/**
 * "View plan" dialog (doc 32 §5) — the ONLY surface where the plan renders as raw JSON: the
 * `andypf-json-viewer` web component with the base16→M3 token mapping proven in
 * workspace-index-card (R3a ruling), at depth 2, with a copy-to-clipboard action that
 * stringifies the raw plan. Everything else in the plan card stays structured; this dialog is
 * a debug aid opened from the plan card's debug section.
 */
@Component({
  selector: "app-plan-json-dialog",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose, MatButton, MatIconButton, MatIcon],
  templateUrl: "./plan-json-dialog.component.html",
  styleUrl: "./plan-json-dialog.component.scss",
})
export class PlanJsonDialogComponent {
  /** Raw contract Plan aggregate injected via `MAT_DIALOG_DATA` (doc 31 §5). */
  protected readonly plan = inject<PlanData>(MAT_DIALOG_DATA);

  /** Pretty-printed dump — the dialog data is immutable input, so the text is built once. */
  protected readonly json = JSON.stringify(this.plan, null, 2);

  /**
   * Copies the JSON dump to the clipboard. Failures are logged, never thrown (repo error rule) —
   * the dialog stays open so the user can select and copy manually.
   */
  protected async copyJson(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.json);
    } catch (error) {
      console.error("plan-json-dialog: clipboard copy failed", error);
    }
  }
}
